import { upsertRoll } from "@/pages/rollshop/repositories/OperationRepository";
import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { Button, CircularProgress, Grid2, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ChangeEvent, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Roll } from "../../models/Roll";
import { LocationTreeSelector } from "../LocationTreeSelector";

type Props = {
	show: boolean;
	onHide: (newDiameter?: number) => void;
	roll: Roll;
};

export const EditRollModal = ({ show, onHide, roll }: Props) => {
	const [error, setError] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const { control, reset, watch } = useForm<Roll>({
		mode: "onChange",
	});

	const diameter = watch("Diameter");
	const locationId = watch("LocationId");

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(
		() => {
			setIsLoading(true);
			return upsertRoll({
				partId: roll.PartId!,
				diameter: diameter,
				locationId: locationId,
				statusCode: "R",
			});
		},
		{
			onSuccess: async (id: number) => {
				await queryClient.invalidateQueries({ queryKey: ["rolls"] });
				onHide(Number(diameter));
			},
			onError: (error: Error) => {
				setError(error.message);
			},
			onSettled: () => setIsLoading(false),
		},
	);

	const onChangeRealType = (
		e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		onChange: (value: string) => void,
	) => {
		const value = e.target.value.replace(",", ".");
		if (value === "" || /^(\d*\.?\d{0,3}|\d+\.)$/.test(value)) {
			onChange(value);
		}
	};
	const onBlurRealType = (onChange: (value: string) => void, value: string) => {
		const numValue = parseFloat(value);
		if (!isNaN(numValue)) {
			onChange(numValue.toFixed(3));
		} else if (value === "") {
			onChange("");
		}
	};

	useEffect(() => {
		reset(roll);
	}, [roll, show]);

	return (
		<>
			<MesfModal
				title={`EDIT ROLL: ${roll?.PartName}`}
				open={show}
				handleClose={() => onHide()}
				id="edit-roll-modal"
				maxWidth="sm"
			>
				<MesfModal.Content dividers>
					<Grid2 container spacing={1} justifyContent="center">
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Controller
								name="LocationId"
								control={control}
								render={({ field }) => (
									<LocationTreeSelector
										label="Location"
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							<Controller
								name="Diameter"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Diameter"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={(e) => onChangeRealType(e, onChange)}
										onBlur={() => onBlurRealType(onChange, String(value))}
										error={!!error}
										helperText={error?.message}
										slotProps={{
											htmlInput: {
												"data-test-id": "diameter-input",
											},
										}}
									/>
								)}
							/>
						</Grid2>
					</Grid2>
				</MesfModal.Content>
				<MesfModal.Actions style={{ padding: "20px 20px 30px" }}>
					<Grid2 container spacing={1} justifyContent="flex-end">
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								variant="contained"
								color="inherit"
								onClick={() => onHide()}
								size="medium"
							>
								Cancel
							</Button>
						</Grid2>
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								fullWidth
								startIcon={isLoading && <CircularProgress size="1rem" />}
								disabled={isLoading}
								variant="contained"
								color="primary"
								size="medium"
								onClick={() => upsertMutation.mutate()}
								data-test-id="confirm-set-ready-button"
							>
								Save
							</Button>
						</Grid2>
					</Grid2>
				</MesfModal.Actions>
			</MesfModal>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error upserting roll"
			/>
		</>
	);
};
